import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  FolderKanban, Cpu, FileText, Activity, Library, Shield, ExternalLink, Layers,
  ArrowLeft, CheckCircle2, AlertCircle, Clock, ShieldCheck, Settings, ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import KnowledgeBase from "@/components/KnowledgeBase";
import ModuleControlPlane from "@/components/ModuleControlPlane";
import { CatalogCards, CatalogArea, resolveCatalog, FamilyItems, EmbeddedItem } from "@/components/workspace/CatalogArea";
import RoutingInteractionsPanel from "@/components/workspace/RoutingInteractionsPanel";
export { default as ProjectsArea } from "@/components/workspace/ProjectsArea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── crash isolation for reused components ──────────────────────────────────────
class AreaBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: false }; }
  static getDerivedStateFromError() { return { err: true }; }
  render() {
    if (this.state.err) {
      return (
        <div className="p-8 text-sm text-[var(--qn-foreground-muted)]" data-testid="area-boundary-error">
          This panel could not be rendered here. Open it from the classic dashboard.
        </div>
      );
    }
    return this.props.children;
  }
}

function AreaHeader({ icon: Icon, title, subtitle, right }) {
  return (
    <div className="flex items-start justify-between px-8 pt-8 pb-4">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[var(--qn-primary-muted)] flex items-center justify-center">
          <Icon className="w-5 h-5 text-[var(--qn-primary)]" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-[var(--qn-foreground-muted)] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {right}
    </div>
  );
}

// ── Projects ───────────────────────────────────────────────────────────────────
// ── Optimization (module-aware convergence, governed) ───────────────────────────
// HARVEST, HAVEN, Grant portfolio optimization and future Modules converge here on
// the SAME canonical execution + Semantic Zoom viewer — no separate apps, no
// duplicated navigation. The frontend owns a FIXED audited registry mapping a
// module_id to its shared migrated-execution endpoint (server never supplies routes).
const MODULE_EXEC_REGISTRY = {
  harvest: "/harvest/convergence/migrated/runs",
  haven: "/haven/convergence/migrated/runs",
  grant_portfolio: "/grant/convergence/migrated/runs",
};
const MODULE_LABELS = {
  harvest: "HARVEST", haven: "HAVEN", grant_portfolio: "Grant Portfolio",
};

export function OptimizationArea({ sub }) {
  const navigate = useNavigate();
  const location = useLocation();
  const cat = resolveCatalog("optimization", sub);
  const [entry, setEntry] = useState(null);
  const [selected, setSelected] = useState(null);   // null = all modules
  const [intent, setIntent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/workspace/optimization/entry`);
      setEntry(data);
    } catch { setEntry({ modules: [], known_modules: [], tasks: [], runs: [] }); }
  };
  useEffect(() => { load(); }, []);

  const activeIds = new Set((entry?.modules || []).map((m) => m.module_id));
  const known = entry?.known_modules || [];
  const highlightTask = location.state?.taskId;

  const isActive = (mid) => activeIds.has(mid);
  const view = (mid) => (selected ? mid === selected : true);
  const tasks = (entry?.tasks || []).filter((t) => view(t.module_context));
  const runs = (entry?.runs || []).filter((r) => view(r.module_id));

  const createTask = async () => {
    if (!selected || !isActive(selected) || !intent.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/workspace/optimization/tasks`, {
        module_id: selected, intent: intent.trim(),
      });
      toast.success(`Draft Task created for ${MODULE_LABELS[selected] || selected}`);
      setIntent("");
      await load();
    } catch (e) {
      const d = e?.response?.data;
      toast.error(d?.message || d?.error || "Could not create the optimization task.");
    } finally { setBusy(false); }
  };

  const runMigrated = async (task) => {
    const endpoint = MODULE_EXEC_REGISTRY[task.module_context];
    if (!endpoint) { toast.error("This Module has no converged execution endpoint."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}${endpoint}`, { task_id: task.task_id });
      toast.success("Migrated Run started on the shared canonical path.");
      if (data?.run_id) navigate(`/app/optimization/runs/${data.run_id}`);
      else await load();
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data;
      const msg = d?.message || d?.error || "Migrated execution is not available yet.";
      toast.error(String(msg));
    } finally { setBusy(false); }
  };

  if (cat.view !== "families") {
    return (
      <div className="min-h-screen" data-testid="optimization-area">
        <AreaHeader icon={Cpu} title="Optimization"
          subtitle="Every optimization experience — HAVEN, HARVEST, Retail, Pharma, Licensing, Grants, Private Equity — lives here." />
        {cat.view === "family" && <FamilyItems areaKey="optimization" family={cat.family} />}
        {cat.view === "item" && <EmbeddedItem areaKey="optimization" family={cat.family} item={cat.item} />}
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="optimization-area">
      <AreaHeader icon={Cpu} title="Optimization"
        subtitle="Governed enterprise optimization — HARVEST, HAVEN and Grant portfolio converge on one canonical execution + viewer." />
      <CatalogCards areaKey="optimization" heading="Optimization experiences" testid="optimization-cards" />
      <div className="px-8 pb-10 space-y-6">
        {/* Module selector — active installations vs known convergence Modules */}
        <div data-testid="optimization-module-selector">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-3">
            Modules
          </h2>
          {known.length === 0 && (
            <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-6" data-testid="optimization-no-module">
              <div className="flex items-center gap-2 font-medium mb-1">
                <AlertCircle className="w-4 h-4 text-amber-500" /> No optimization Module available
              </div>
              <p className="text-sm text-[var(--qn-foreground-muted)] mb-3">
                Your tenant can still use Chat, Projects, Knowledge, Files and Artifacts.
                An administrator must install an optimization Module before Tasks can run.
              </p>
              <Button size="sm" variant="outline" onClick={() => navigate("/app/modules")} className="gap-1.5"
                data-testid="optimization-request-access">
                Request optimization access <ArrowLeft className="w-3.5 h-3.5 rotate-180" />
              </Button>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button data-testid="optimization-module-all" onClick={() => setSelected(null)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                selected === null ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]"
                  : "border-[var(--qn-border)] bg-[var(--qn-card)] text-[var(--qn-foreground-muted)]"}`}>
              All Modules
            </button>
            {known.map((mid) => (
              <button key={mid} data-testid={`optimization-module-${mid}`} onClick={() => setSelected(mid)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                  selected === mid ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]"
                    : "border-[var(--qn-border)] bg-[var(--qn-card)] text-[var(--qn-foreground-muted)]"}`}>
                <Layers className={`w-3.5 h-3.5 ${isActive(mid) ? "text-emerald-500" : "text-zinc-400"}`} />
                {MODULE_LABELS[mid] || mid}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${isActive(mid)
                  ? "bg-emerald-500/10 text-emerald-500" : "bg-[var(--qn-surface)] text-[var(--qn-foreground-subtle)]"}`}>
                  {isActive(mid) ? "active" : "not installed"}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Module-aware Task entry */}
        {selected && isActive(selected) && (
          <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4" data-testid="optimization-new-task">
            <div className="text-sm font-semibold mb-2">New {MODULE_LABELS[selected] || selected} optimization task</div>
            <div className="flex gap-2">
              <input value={intent} onChange={(e) => setIntent(e.target.value)}
                data-testid="optimization-new-task-input"
                placeholder="Describe the decision to optimize (inputs & constraints reviewed before anything runs)…"
                className="flex-1 rounded-lg border border-[var(--qn-border)] bg-[var(--qn-surface)] px-3 py-2 text-sm outline-none focus:border-[var(--qn-primary)]" />
              <Button size="sm" onClick={createTask} disabled={busy || !intent.trim()}
                data-testid="optimization-new-task-submit">Create draft</Button>
            </div>
            <div className="text-xs text-[var(--qn-foreground-subtle)] mt-2">
              Creates a governed canonical Task (module_context <span className="font-mono">{selected}</span>). Review scenario, policy and approval before migrated execution.
            </div>
          </div>
        )}

        {/* Blueprint-vs-runtime truthful note */}
        <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-surface)]/40 p-4" data-testid="optimization-blueprint-note">
          <div className="text-xs text-[var(--qn-foreground-muted)]">
            Registered blueprints are the organizational catalog — only the Work Units selected for a specific Run become active runtime work.
            Open any Run to see selected vs. blueprint, solver provenance, provider routing, limitations and economics with truthful
            <span className="font-medium"> unavailable</span> states where canonical Evidence is absent.
          </div>
        </div>

        {/* Canonical Tasks */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-3">
            Optimization tasks{selected ? ` · ${MODULE_LABELS[selected] || selected}` : ""}
          </h2>
          {tasks.length === 0 && (
            <div className="text-sm text-[var(--qn-foreground-muted)]" data-testid="optimization-tasks-empty">
              No optimization tasks yet. Select an active Module and create a draft, or ask an optimization question in Chat.
            </div>
          )}
          <div className="space-y-3">
            {tasks.map((t) => {
              const approved = Boolean(t.approved_scenario_digest);
              const canRun = approved && isActive(t.module_context) && MODULE_EXEC_REGISTRY[t.module_context];
              return (
                <div key={t.task_id} data-testid={`optimization-task-${t.task_id}`}
                  className={`rounded-xl border p-4 ${t.task_id === highlightTask
                    ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)]"
                    : "border-[var(--qn-border)] bg-[var(--qn-card)]"}`}>
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Clock className="w-4 h-4 text-[var(--qn-primary)]" />
                    <span className="text-xs font-mono text-[var(--qn-foreground-subtle)]">{t.task_id}</span>
                    <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                      {MODULE_LABELS[t.module_context] || t.module_context}
                    </span>
                    <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">
                      {t.status === "open" ? "draft" : t.status}
                    </span>
                    {approved && <span className="inline-flex items-center gap-1 text-[11px] text-emerald-500"><CheckCircle2 className="w-3.5 h-3.5" /> approved</span>}
                    {canRun && (
                      <Button size="sm" variant="outline" className="ml-auto" disabled={busy}
                        data-testid={`optimization-run-migrated-${t.task_id}`} onClick={() => runMigrated(t)}>
                        Run migrated
                      </Button>
                    )}
                  </div>
                  <div className="text-sm text-[var(--qn-foreground)]">{t.intent}</div>
                  {!approved && (
                    <div className="text-xs text-[var(--qn-foreground-subtle)] mt-2">
                      Governed draft — review inputs, policy and constraints, then approve before migrated execution.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Canonical Runs — deep-link into the shared Semantic Zoom viewer */}
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-3">
            Runs{selected ? ` · ${MODULE_LABELS[selected] || selected}` : ""}
          </h2>
          {runs.length === 0 && (
            <div className="text-sm text-[var(--qn-foreground-muted)]" data-testid="optimization-runs-empty">No runs yet.</div>
          )}
          <div className="space-y-2">
            {runs.map((r) => (
              <button key={r.run_id} data-testid={`optimization-run-${r.run_id}`}
                onClick={() => navigate(`/app/optimization/runs/${r.run_id}`)}
                className="w-full text-left rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3 hover:border-[var(--qn-primary)]/40 transition-colors flex items-center gap-3">
                <StatusDot status={r.status} />
                <span className="font-mono text-xs text-[var(--qn-foreground-subtle)]">{r.run_id}</span>
                <span className="text-sm">{MODULE_LABELS[r.module_id] || r.module_id}</span>
                <span className="ml-auto inline-flex items-center gap-1 text-xs text-[var(--qn-primary)]">
                  <Activity className="w-3.5 h-3.5" /> Open viewer
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Runs ─────────────────────────────────────────────────────────────────────
export function RunsArea() {
  const [runs, setRuns] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/canonical/runs`); setRuns(data.runs || []); }
      catch { /* noop */ } finally { setLoading(false); }
    })();
  }, []);
  const open = async (id) => {
    try { const { data } = await axios.get(`${API}/canonical/runs/${id}`); setDetail(data); }
    catch { /* noop */ }
  };
  if (detail) {
    const r = detail.run || {};
    return (
      <div className="min-h-screen" data-testid="runs-detail">
        <AreaHeader icon={Activity} title="Run detail" subtitle={r.run_id}
          right={<Button variant="outline" size="sm" onClick={() => setDetail(null)} className="gap-1.5"><ArrowLeft className="w-3.5 h-3.5" /> Back</Button>} />
        <div className="px-8 pb-10 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["Status", r.status], ["Module", r.module_id], ["Task", r.task_id],
              ["Integrity", detail.integrity?.ok ? "verified" : "check"]].map(([k, v]) => (
              <div key={k} className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3">
                <div className="text-[11px] uppercase text-[var(--qn-foreground-subtle)]">{k}</div>
                <div className="text-sm font-medium mt-0.5 truncate">{String(v ?? "—")}</div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4">
            <div className="text-sm font-semibold mb-2">Event timeline ({detail.events?.length || 0})</div>
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {(detail.events || []).map((e, i) => (
                <div key={i} className="text-xs font-mono text-[var(--qn-foreground-muted)] flex gap-3">
                  <span className="text-[var(--qn-foreground-subtle)]">#{e.sequence}</span>
                  <span className="text-[var(--qn-primary)]">{e.event_type}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniList title={`Work units (${detail.work_units?.length || 0})`} items={(detail.work_units || []).map((w) => w.capability || w.work_unit_id)} />
            <MiniList title={`Artifacts (${detail.artifacts?.length || 0})`} items={(detail.artifacts || []).map((a) => `${a.kind} · v${a.version}`)} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen" data-testid="runs-area">
      <AreaHeader icon={Activity} title="Runs" subtitle="Enterprise execution history across every Module." />
      <div className="px-8 pb-10">
        {loading && <div className="text-sm text-[var(--qn-foreground-muted)]">Loading…</div>}
        {!loading && runs.length === 0 && <div className="text-sm text-[var(--qn-foreground-muted)]">No runs yet.</div>}
        <div className="space-y-2">
          {runs.map((r) => (
            <button key={r.run_id} data-testid={`run-row-${r.run_id}`} onClick={() => open(r.run_id)}
              className="w-full text-left rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3 hover:border-[var(--qn-primary)]/40 transition-colors flex items-center gap-3">
              <StatusDot status={r.status} />
              <span className="font-mono text-xs text-[var(--qn-foreground-subtle)]">{r.run_id}</span>
              <span className="text-sm">{r.module_id}</span>
              <span className="ml-auto text-xs text-[var(--qn-foreground-subtle)]">{(r.created_at || "").slice(0, 19).replace("T", " ")}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusDot({ status }) {
  const color = status === "completed" ? "bg-emerald-500" : status === "failed" ? "bg-red-500"
    : status === "running" ? "bg-blue-500" : "bg-zinc-400";
  return <span className={`w-2.5 h-2.5 rounded-full ${color}`} />;
}
function MiniList({ title, items }) {
  return (
    <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-4">
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {items.length === 0 && <div className="text-xs text-[var(--qn-foreground-subtle)]">None</div>}
        {items.map((it, i) => <div key={i} className="text-xs text-[var(--qn-foreground-muted)]">{it}</div>)}
      </div>
    </div>
  );
}

// ── Reports ────────────────────────────────────────────────────────────────────
export function ReportsArea({ sub }) {
  const cat = resolveCatalog("reports", sub);
  const [reports, setReports] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const loadInbox = async () => {
    try {
      const a = await axios.get(`${API}/workspace/approvals/inbox`);
      setApprovals(a.data.approvals || []); setIsAdmin(true);
    } catch { setApprovals([]); setIsAdmin(false); }  // 403 for non-admins is expected
  };
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/workspace/reports`);
        setReports(r.data.reports || []);
      } catch { /* noop */ }
      await loadInbox();
      setLoading(false);
    })();
  }, []);
  const act = async (id, verb) => {
    try {
      await axios.post(`${API}/workspace/approvals/${id}/${verb}`);
      toast.success(`Approval ${verb}${verb.endsWith("e") ? "d" : "ed"}`);
      await loadInbox();
    } catch (e) {
      const detail = e?.response?.data;
      const msg = detail?.message || detail?.error || `Could not ${verb} this approval`;
      toast.error(msg);
    }
  };
  return (
    <div className="min-h-screen" data-testid="reports-area">
      <AreaHeader icon={FileText} title="Reports" subtitle="Savings, overview, spend protection and governed reports — available to every tenant." />
      {cat.view === "family" && <FamilyItems areaKey="reports" family={cat.family} />}
      {cat.view === "item" && <EmbeddedItem areaKey="reports" family={cat.family} item={cat.item} />}
      {cat.view === "families" && <CatalogCards areaKey="reports" heading="Savings & reports" testid="reports-cards" />}
      <div className="px-8 pb-10 space-y-6" hidden={cat.view !== "families"}>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-3">Approval inbox</h2>
          {approvals.length === 0 && <div className="text-sm text-[var(--qn-foreground-muted)]" data-testid="approvals-empty">No consequential-action approvals pending.</div>}
          <div className="space-y-2">
            {approvals.map((a) => (
              <div key={a.approval_id} data-testid={`approval-row-${a.approval_id}`}
                className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3 flex items-center gap-3 flex-wrap">
                <ShieldCheck className="w-4 h-4 text-[var(--qn-primary)]" />
                <span className="text-sm">{a.action || a.resource_kind}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)] font-mono">digest {String(a.action_digest || "").slice(0, 10)}…</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">{a.status}</span>
                <span className="text-xs text-[var(--qn-foreground-subtle)]">by {String(a.requester || "").slice(0, 12)}</span>
                {isAdmin && a.status === "pending" && (
                  <span className="ml-auto flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`approval-approve-${a.approval_id}`} onClick={() => act(a.approval_id, "approve")}>Approve</Button>
                    <Button size="sm" variant="outline" data-testid={`approval-reject-${a.approval_id}`} onClick={() => act(a.approval_id, "reject")}>Reject</Button>
                    <Button size="sm" variant="outline" data-testid={`approval-revoke-${a.approval_id}`} onClick={() => act(a.approval_id, "revoke")}>Revoke</Button>
                  </span>
                )}
                {isAdmin && a.status === "approved" && (
                  <span className="ml-auto text-xs text-[var(--qn-foreground-subtle)]">approved by {String(a.approver || "").slice(0, 12)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-3">Reports</h2>
          {loading && <div className="text-sm text-[var(--qn-foreground-muted)]">Loading…</div>}
          {!loading && reports.length === 0 && <div className="text-sm text-[var(--qn-foreground-muted)]">No reports yet.</div>}
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.artifact_id} data-testid={`report-row-${r.artifact_id}`}
                className="rounded-lg border border-[var(--qn-border)] bg-[var(--qn-card)] p-3 flex items-center gap-3">
                <FileText className="w-4 h-4 text-[var(--qn-primary)]" />
                <span className="text-sm">{r.report_key || "report"}</span>
                <span className="text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">{r.classification}</span>
                <span className="text-xs text-[var(--qn-foreground-subtle)]">v{r.version} · {r.module_id}</span>
                <span className="ml-auto text-xs text-[var(--qn-foreground-subtle)] font-mono">{r.run_id}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Knowledge (reuse) ────────────────────────────────────────────────────────
export function KnowledgeArea() {
  return (
    <div className="min-h-screen" data-testid="knowledge-area">
      <AreaHeader icon={Library} title="Knowledge" subtitle="Individual, project, team, organization and Module knowledge." />
      <div className="px-4 pb-10">
        <AreaBoundary><KnowledgeBase /></AreaBoundary>
      </div>
    </div>
  );
}

// ── Modules (tenant view; control plane only for platform admins) ──────────────
export function ModulesArea({ identity, nav }) {
  const [data, setData] = useState(null);
  const isPA = identity?.is_platform_admin;
  useEffect(() => {
    if (isPA) return;
    (async () => {
      try { const { data } = await axios.get(`${API}/modules/mine`); setData(data); }
      catch { setData({ installations: [], entitlements: [] }); }
    })();
  }, [isPA]);

  if (isPA) {
    return (
      <div className="min-h-screen" data-testid="modules-area">
        <AreaHeader icon={Layers} title="Modules" subtitle="Tenant Module configuration and health (platform control plane)." />
        <div className="px-4 pb-10"><AreaBoundary><ModuleControlPlane /></AreaBoundary></div>
      </div>
    );
  }

  const installs = data?.installations || [];
  const installedIds = new Set(installs.map((i) => i.module_id));
  const entMap = {};
  (data?.entitlements || []).forEach((e) => { entMap[e.module_id] = e; });
  // Legacy fail-open access surfaced in the shell nav but not an explicit install.
  const legacy = (nav?.modules || []).filter((m) => !installedIds.has(m.module_id));
  const health = (s) => s === "active" ? "text-emerald-500" : s === "suspended" ? "text-amber-500" : "text-zinc-400";
  const total = installs.length + legacy.length;
  return (
    <div className="min-h-screen" data-testid="modules-area">
      <AreaHeader icon={Layers} title="Modules" subtitle="Your tenant's Modules and health." />
      <div className="px-8 pb-10">
        {!data ? <div className="text-sm text-[var(--qn-foreground-muted)]">Loading…</div> : (
        <>
        {total === 0 && (
          <div className="text-sm text-[var(--qn-foreground-muted)]" data-testid="modules-empty">
            No Modules are available to your tenant yet. Ask a platform administrator to install one.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {installs.map((i) => {
            const ent = entMap[i.module_id];
            return (
              <div key={i.module_id} data-testid={`module-card-${i.module_id}`}
                className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Layers className={`w-4 h-4 ${health(i.status)}`} />
                  <span className="font-medium">{i.module_id}</span>
                  <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">{i.status}</span>
                </div>
                <div className="text-xs text-[var(--qn-foreground-subtle)]">v{i.module_version} · health {i.health || "unknown"}</div>
                {ent && <div className="text-xs text-[var(--qn-foreground-muted)] mt-2">Entitlement: {ent.state}</div>}
              </div>
            );
          })}
          {legacy.map((m) => (
            <div key={m.module_id} data-testid={`module-card-${m.module_id}`}
              className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5">
              <div className="flex items-center gap-2 mb-1">
                <Layers className="w-4 h-4 text-emerald-500" />
                <span className="font-medium">{m.label || m.module_id}</span>
                <span className="ml-auto text-[11px] px-2 py-0.5 rounded bg-[var(--qn-surface)] border border-[var(--qn-border)]">enabled</span>
              </div>
              <div className="text-xs text-[var(--qn-foreground-subtle)]">Legacy entitlement · pending explicit installation</div>
            </div>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

// ── Admin ──────────────────────────────────────────────────────────────────────
export function AdminArea({ identity }) {
  const navigate = useNavigate();
  const links = [
    { label: "Tenant Admin", desc: "Users, teams, connectors, policies, data controls, audit", to: "/dashboard", tid: "admin-tenant" },
    { label: "Settings", desc: "Workspace and account settings", to: "/settings", tid: "admin-settings" },
  ];
  if (identity?.is_platform_admin) {
    links.unshift({ label: "Platform Admin", desc: "Platform-wide control plane (root)", to: "/platform-admin", tid: "admin-platform" });
  }
  return (
    <div className="min-h-screen" data-testid="admin-area">
      <AreaHeader icon={Shield} title="Admin" subtitle="Users, teams, connectors, policies, data controls and audit." />
      <div className="px-8 pb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {links.map((l) => (
          <button key={l.tid} data-testid={l.tid} onClick={() => navigate(l.to)}
            className="text-left rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] p-5 hover:border-[var(--qn-primary)]/40 transition-colors">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-4 h-4 text-[var(--qn-primary)]" />
              <span className="font-medium">{l.label}</span>
              <ExternalLink className="w-3.5 h-3.5 ml-auto text-[var(--qn-foreground-subtle)]" />
            </div>
            <div className="text-sm text-[var(--qn-foreground-muted)]">{l.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}


// ── Settings (all tenants) + Platform Admin (platform owners only) ─────────────
export function SettingsArea({ sub }) {
  return <CatalogArea areaKey="settings" sub={sub}
    header={<AreaHeader icon={Settings} title="Settings" subtitle="Account, profile, plan & billing, team, usage and alerts." />} />;
}

export function PlatformAdminArea({ sub }) {
  const header = (
    <AreaHeader icon={ShieldAlert} title="Platform Admin"
      subtitle="Platform-owner console — root controls, engine, workflows and governance. Not visible to tenants." />
  );
  if (sub === "routing-interactions") {
    return (
      <div className="min-h-screen" data-testid="platform-admin-area">
        {header}
        <BackToAll to="/app/platform-admin" />
        <RoutingInteractionsPanel />
      </div>
    );
  }
  return <CatalogArea areaKey="platform-admin" sub={sub} header={header} />;
}

function BackToAll({ to }) {
  const navigate = useNavigate();
  return (
    <div className="px-8 pb-3">
      <button onClick={() => navigate(to)} data-testid="catalog-back"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--qn-foreground-muted)] hover:text-[var(--qn-primary)]">
        <ArrowLeft className="w-4 h-4" /> All
      </button>
    </div>
  );
}
